import { createContext, useContext } from 'react';
import { createNoopClient, type AnalyticsClient } from './client';
import type { EventName, EventPayload } from './types';

/**
 * React context handle for the singleton analytics client. Kept in its
 * own non-component module so the `provider.tsx` file only exports React
 * components — Fast Refresh requires that.
 */
export const AnalyticsContext = createContext<AnalyticsClient | null>(null);

/**
 * Internal hook. Components should prefer `useTrack()`, which exposes
 * the type-safe `track` function only. Fails safe: a component rendered
 * outside the provider gets a no-op client rather than crashing.
 */
export function useAnalyticsClient(): AnalyticsClient {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) {
    if (import.meta.env.DEV) {
      console.warn('[analytics] useAnalyticsClient called outside AnalyticsProvider — returning no-op');
    }
    return createNoopClient();
  }
  return ctx;
}

export type TrackFn = <E extends EventName>(name: E, properties: EventPayload<E>) => void;

/**
 * Component-facing hook. Returns the type-safe `track` function only.
 *
 *   const track = useTrack();
 *   track('page_viewed', { path: '/wallet' });
 */
export function useTrack(): TrackFn {
  return useAnalyticsClient().track;
}
